import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Input, Switch, message } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { kioskSurveyUrl } from "./surveyApiHelpers";
import { setLinkKiosk } from "./surveyService";
import styles from "./style.module.scss";

/**
 * "Kiosk mode" on the Link & QR card: a tablet at a desk collects a response from everyone who walks
 * up. Saved right away (like "Edit link"); switching it off stops every kiosk link. Only for a saved
 * survey with a link. Timings come from pivony-api survey_kiosk (5 s after a submit, 60 s idle).
 */
const KioskModeToggle = ({ instrumentId, slug, fullUrl, kioskKey, onChange }) => {
  const { t } = useTranslation();
  const [saving, setSaving] = useState(false);
  const canSwitch = !!(instrumentId && slug);
  const kioskUrl = kioskSurveyUrl(fullUrl, kioskKey);

  const toggle = async (enabled) => {
    setSaving(true);
    try {
      const res = await setLinkKiosk(instrumentId, enabled);
      if (res?.channel) {
        onChange(res.channel.config?.kiosk_key || "");
        message.success(
          t(enabled ? "surveyManagement.distribute.kiosk.enabled_toast" : "surveyManagement.distribute.kiosk.disabled_toast")
        );
        return;
      }
      message.error(t("surveyManagement.msg_save_error"));
    } catch (_) {
      message.error(t("surveyManagement.msg_save_error"));
    } finally {
      setSaving(false);
    }
  };

  const copy = () => {
    if (!kioskUrl) return;
    navigator.clipboard
      .writeText(kioskUrl)
      .then(() => message.success(t("surveyManagement.distribute.link.copied")))
      .catch(() => message.error(t("surveyManagement.msg_save_error")));
  };

  return (
    <div className={styles.kioskBox}>
      <div className={styles.kioskHeader}>
        <Switch checked={!!kioskKey} loading={saving} disabled={!canSwitch} onChange={toggle} />
        <div>
          <div className={styles.kioskTitle}>{t("surveyManagement.distribute.kiosk.title")}</div>
          <p className={styles.fieldHint}>{t("surveyManagement.distribute.kiosk.description")}</p>
        </div>
      </div>
      {!canSwitch ? <p className={styles.fieldHint}>{t("surveyManagement.distribute.kiosk.save_first")}</p> : null}
      {kioskUrl ? (
        <>
          <div className={styles.distLinkRow}>
            <Input readOnly value={kioskUrl} aria-label={t("surveyManagement.distribute.kiosk.link_label")} />
            <Button icon={<CopyOutlined />} onClick={copy}>
              {t("surveyManagement.distribute.link.copy")}
            </Button>
          </div>
          <p className={styles.fieldHint}>{t("surveyManagement.distribute.kiosk.how_to")}</p>
        </>
      ) : null}
    </div>
  );
};

export default KioskModeToggle;
